import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Brain, 
  Trophy, 
  Clock, 
  DollarSign,
  Users,
  Zap,
  Star,
  Award,
  Timer,
  BarChart3,
  Eye,
  Gift
} from 'lucide-react';

interface Prediction {
  id: string;
  title: string;
  description: string;
  category: string;
  endDate: Date;
  totalPool: number;
  participants: number;
  userPrediction?: 'up' | 'down' | null;
  userStake?: number;
  currentPrice: number;
  targetPrice?: number;
  timeFrame: string;
  aiConfidence: number;
  bullishVotes: number;
  bearishVotes: number;
  reward: string;
}

interface Tournament {
  id: string;
  name: string;
  description: string;
  entryFee: number;
  prizePool: number;
  participants: number;
  maxParticipants: number;
  startDate: Date;
  endDate: Date;
  status: 'upcoming' | 'active' | 'ended';
  category: string;
}

export function MarketPredictionArena() {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1h');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [predictionAmount, setPredictionAmount] = useState('');

  const activePredictions: Prediction[] = [
    {
      id: '1',
      title: 'BTC Price Movement',
      description: 'Will Bitcoin break above $45,000 in the next 24 hours?',
      category: 'Bitcoin',
      endDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
      totalPool: 5420,
      participants: 234,
      currentPrice: 43850,
      targetPrice: 45000,
      timeFrame: '24h',
      aiConfidence: 72,
      bullishVotes: 60,
      bearishVotes: 40,
      reward: '500 NDH + AI NFT',
      userPrediction: 'up',
      userStake: 100
    },
    {
      id: '2',
      title: 'ETH vs BTC Pair',
      description: 'Will ETH/BTC ratio increase by 5% this week?',
      category: 'Ethereum',
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      totalPool: 3250,
      participants: 187,
      currentPrice: 0.061,
      timeFrame: '7d',
      aiConfidence: 64,
      bullishVotes: 55,
      bearishVotes: 45,
      reward: '300 NDH'
    },
    {
      id: '3',
      title: 'DeFi TVL Milestone',
      description: 'Will Total Value Locked in DeFi exceed $100B this month?',
      category: 'DeFi',
      endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
      totalPool: 8750,
      participants: 412,
      currentPrice: 87.5,
      timeFrame: '30d',
      aiConfidence: 81,
      bullishVotes: 75,
      bearishVotes: 25,
      reward: '1000 NDH + Legendary NFT'
    }
  ];

  const tournaments: Tournament[] = [
    {
      id: '1',
      name: 'Weekly Prediction Championship',
      description: 'Predict 5 major crypto movements this week',
      entryFee: 50,
      prizePool: 10000,
      participants: 156,
      maxParticipants: 500,
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      status: 'active',
      category: 'General'
    },
    {
      id: '2',
      name: 'AI vs Humans Challenge',
      description: 'Compete against our AI trading bot in prediction accuracy',
      entryFee: 100,
      prizePool: 25000,
      participants: 89,
      maxParticipants: 200,
      startDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
      endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      status: 'upcoming',
      category: 'AI Challenge'
    }
  ];

  const getTimeLeft = (endDate: Date) => {
    const now = new Date();
    const diff = endDate.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const getAIConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'text-green-600 bg-green-100 dark:bg-green-900';
    if (confidence >= 60) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900';
    return 'text-red-600 bg-red-100 dark:bg-red-900';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2">
            <Target className="h-6 w-6" />
            Market Prediction Arena
          </h2>
          <p className="text-muted-foreground">
            Compete in prediction tournaments and earn NDH rewards
          </p>
        </div>
        <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
          AI-Powered
        </Badge>
      </div>

      <Tabs defaultValue="predictions" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="predictions">Active Predictions</TabsTrigger>
          <TabsTrigger value="tournaments">Tournaments</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="predictions" className="space-y-6">
          {/* Filters */}
          <div className="flex gap-4">
            <div className="flex gap-2">
              {['1h', '24h', '7d', '30d'].map((timeframe) => (
                <Button
                  key={timeframe}
                  variant={selectedTimeframe === timeframe ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedTimeframe(timeframe)}
                >
                  {timeframe}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              {['all', 'Bitcoin', 'Ethereum', 'DeFi'].map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {/* Active Predictions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {activePredictions.map((prediction) => (
              <Card key={prediction.id} className="relative overflow-hidden">
                <div className="absolute top-0 right-0 p-2">
                  <Badge className={getAIConfidenceColor(prediction.aiConfidence)}>
                    <Brain className="h-3 w-3 mr-1" />
                    AI: {prediction.aiConfidence}%
                  </Badge>
                </div>

                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">{prediction.title}</CardTitle>
                      <CardDescription>{prediction.description}</CardDescription>
                      <Badge variant="secondary">{prediction.category}</Badge>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Current Stats */}
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-lg font-bold">${prediction.totalPool}</p>
                      <p className="text-sm text-muted-foreground">Total Pool</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold">{prediction.participants}</p>
                      <p className="text-sm text-muted-foreground">Participants</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-orange-500">
                        <Clock className="h-4 w-4 inline mr-1" />
                        {getTimeLeft(prediction.endDate)}
                      </p>
                      <p className="text-sm text-muted-foreground">Time Left</p>
                    </div>
                  </div>

                  {/* Price Info */}
                  {prediction.targetPrice && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Current: ${prediction.currentPrice}</span>
                        <span>Target: ${prediction.targetPrice}</span>
                      </div>
                      <Progress 
                        value={(prediction.currentPrice / prediction.targetPrice) * 100} 
                        className="h-2" 
                      />
                    </div>
                  )}

                  {/* Voting Distribution */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600 flex items-center gap-1">
                        <TrendingUp className="h-4 w-4" />
                        Bullish {prediction.bullishVotes}%
                      </span>
                      <span className="text-red-600 flex items-center gap-1">
                        <TrendingDown className="h-4 w-4" />
                        Bearish {prediction.bearishVotes}%
                      </span>
                    </div>
                    <div className="flex h-2 rounded-full overflow-hidden">
                      <div 
                        className="bg-green-500" 
                        style={{ width: `${prediction.bullishVotes}%` }}
                      />
                      <div 
                        className="bg-red-500" 
                        style={{ width: `${prediction.bearishVotes}%` }}
                      />
                    </div>
                  </div>

                  {/* User Prediction Status */}
                  {prediction.userPrediction ? (
                    <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Your Prediction:</span>
                        <div className="flex items-center gap-2">
                          <Badge className={prediction.userPrediction === 'up' ? 'bg-green-500' : 'bg-red-500'}>
                            {prediction.userPrediction === 'up' ? (
                              <><TrendingUp className="h-3 w-3 mr-1" />Bullish</>
                            ) : (
                              <><TrendingDown className="h-3 w-3 mr-1" />Bearish</>
                            )}
                          </Badge>
                          <span className="text-sm font-bold">{prediction.userStake} NDH</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input 
                          placeholder="NDH amount"
                          value={predictionAmount}
                          onChange={(e) => setPredictionAmount(e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button className="bg-green-500 hover:bg-green-600">
                          <TrendingUp className="h-4 w-4 mr-2" />
                          Predict UP
                        </Button>
                        <Button className="bg-red-500 hover:bg-red-600">
                          <TrendingDown className="h-4 w-4 mr-2" />
                          Predict DOWN
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <p className="text-sm font-medium text-purple-600">
                      <Gift className="h-4 w-4 inline mr-1" />
                      Reward: {prediction.reward}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="tournaments" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {tournaments.map((tournament) => (
              <Card key={tournament.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Trophy className="h-5 w-5" />
                        {tournament.name}
                      </CardTitle>
                      <CardDescription>{tournament.description}</CardDescription>
                    </div>
                    <Badge 
                      variant={tournament.status === 'active' ? 'default' : 'secondary'}
                      className={tournament.status === 'active' ? 'bg-green-500' : ''}
                    >
                      {tournament.status}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{tournament.prizePool} NDH</p>
                      <p className="text-sm text-muted-foreground">Prize Pool</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{tournament.entryFee} NDH</p>
                      <p className="text-sm text-muted-foreground">Entry Fee</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Participants</span>
                      <span>{tournament.participants}/{tournament.maxParticipants}</span>
                    </div>
                    <Progress 
                      value={(tournament.participants / tournament.maxParticipants) * 100} 
                      className="h-2" 
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium">Start Date</p>
                      <p className="text-muted-foreground">
                        {tournament.startDate.toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">End Date</p>
                      <p className="text-muted-foreground">
                        {tournament.endDate.toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <Button 
                    className="w-full"
                    disabled={tournament.status === 'ended'}
                  >
                    {tournament.status === 'upcoming' ? 'Register' : 
                     tournament.status === 'active' ? 'Join Now' : 'View Results'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Top Predictors This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((rank) => (
                  <div key={rank} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : rank === 3 ? 'bg-orange-500' : 'bg-gray-200'
                      } text-white font-bold text-sm`}>
                        {rank}
                      </div>
                      <div>
                        <p className="font-medium">CryptoProphet_{rank}</p>
                        <p className="text-sm text-muted-foreground">
                          {95 - rank * 5}% accuracy • {50 - rank * 5} predictions
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">+{1000 - rank * 100} NDH</p>
                      <p className="text-sm text-muted-foreground">This month</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-insights" className="space-y-6">
          <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-6 w-6" />
                AI Market Analysis
              </CardTitle>
              <CardDescription className="text-purple-100">
                Real-time insights from our advanced prediction algorithms
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">72%</p>
                  <p className="text-sm opacity-90">Bullish Sentiment</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">8.5/10</p>
                  <p className="text-sm opacity-90">Market Volatility</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">+15%</p>
                  <p className="text-sm opacity-90">Expected Weekly Gain</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">AI Confidence Levels</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { asset: 'Bitcoin', confidence: 85, direction: 'up' },
                  { asset: 'Ethereum', confidence: 72, direction: 'up' },
                  { asset: 'Solana', confidence: 68, direction: 'down' },
                  { asset: 'Cardano', confidence: 45, direction: 'up' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.asset}</span>
                      {item.direction === 'up' ? 
                        <TrendingUp className="h-4 w-4 text-green-500" /> : 
                        <TrendingDown className="h-4 w-4 text-red-500" />
                      }
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-gray-200 rounded-full">
                        <div 
                          className={`h-2 rounded-full ${item.confidence >= 70 ? 'bg-green-500' : 'bg-yellow-500'}`}
                          style={{ width: `${item.confidence}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">{item.confidence}%</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Market Signals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { signal: 'Golden Cross BTC', strength: 'Strong', color: 'green' },
                  { signal: 'Volume Spike ETH', strength: 'Medium', color: 'yellow' },
                  { signal: 'RSI Oversold SOL', strength: 'Weak', color: 'red' },
                  { signal: 'Whale Activity', strength: 'Strong', color: 'green' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border rounded">
                    <span className="font-medium">{item.signal}</span>
                    <Badge className={`bg-${item.color}-500`}>
                      {item.strength}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}